import { useState } from 'react';
import {
  Dialog,
  Button,
  Typography,
  Box,
  Chip,
  Fade,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  RocketLaunch as RocketIcon,
  Chat as ChatIcon,
  MenuBook as DocsIcon,
  ConfirmationNumber as TicketIcon,
} from '@mui/icons-material';
import { COMING_SOON_DISMISSED_KEY } from '@/common-constants/storage-keys';

/** When `false`, the modal never opens (KB / Log Oracle features are live). Set to `true` to gate on dismissal + localStorage again. */
const COMING_SOON_MODAL_ENABLED = false;

function getInitialOpen(): boolean {
  if (!COMING_SOON_MODAL_ENABLED) return false;
  try {
    return !localStorage.getItem(COMING_SOON_DISMISSED_KEY);
  } catch {
    return false;
  }
}

const FEATURES = [
  {
    icon: ChatIcon,
    title: 'AI Support Assistant',
    description: 'Conversational assistant for transaction analysis and troubleshooting.',
    color: '#06b6d4',
  },
  {
    icon: DocsIcon,
    title: 'AI Knowledge Base',
    description: 'Collaborate on AI knowledge by adding, updating, and managing knowledge base articles and documentation.',
    color: '#f59e0b',
  },
  {
    icon: TicketIcon,
    title: 'ServiceNow Integration',
    description: 'Integration with ServiceNow to retrieve open tickets.',
    color: '#8b5cf6',
  },
] as const;

export function ComingSoonModal() {
  const [open, setOpen] = useState(getInitialOpen);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleClose = () => {
    try {
      localStorage.setItem(COMING_SOON_DISMISSED_KEY, 'true');
    } catch {
      // ignore
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 400 }}
      PaperProps={{
        sx: {
          borderRadius: { xs: '16px', sm: '20px' },
          boxShadow: '0 25px 60px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
          margin: { xs: '16px', sm: '32px' },
          maxHeight: { xs: 'calc(100vh - 32px)', sm: 'calc(100vh - 64px)' },
        },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a2a4a 0%, #23395d 40%, #2d4a7c 100%)',
          color: '#fff',
          padding: { xs: '28px 20px', sm: '36px 32px' },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -60,
            right: -60,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -40,
            left: -40,
            width: 140,
            height: 140,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)',
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: '12px' }}>
            <Box
              sx={{
                width: { xs: 40, sm: 44 },
                height: { xs: 40, sm: 44 },
                borderRadius: '12px',
                bgcolor: 'rgba(255,255,255,0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <RocketIcon sx={{ fontSize: { xs: 22, sm: 24 } }} />
            </Box>
            <Chip
              label="Coming Soon"
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 24,
                letterSpacing: '0.5px',
                backdropFilter: 'blur(8px)',
              }}
            />
          </Box>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.35rem', sm: '1.6rem' },
              lineHeight: 1.25,
              letterSpacing: '-0.01em',
              mb: '8px',
            }}
          >
            Welcome to BCP Wizard Ops Portal
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '0.875rem', sm: '0.95rem' },
              opacity: 0.85,
              lineHeight: 1.5,
              whiteSpace: 'nowrap',
            }}
          >
            We&apos;re building powerful features for the Ops Portal. Here&apos;s a look at what&apos;s coming.
          </Typography>
        </Box>
      </Box>

      {/* ── Feature Cards ── */}
      <Box
        sx={{
          padding: { xs: '20px', sm: '28px 32px' },
          bgcolor: '#fafafa',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: { xs: '12px', sm: '14px' },
          }}
        >
          {FEATURES.map(({ icon: Icon, title, description, color }) => (
            <Box
              key={title}
              sx={{
                bgcolor: '#fff',
                borderRadius: '14px',
                padding: { xs: '16px', sm: '20px' },
                border: '1px solid',
                borderColor: alpha(color, 0.15),
                transition: 'all 0.2s ease',
                cursor: 'default',
                '&:hover': {
                  borderColor: alpha(color, 0.35),
                  boxShadow: `0 4px 20px ${alpha(color, 0.1)}`,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: '10px',
                    bgcolor: alpha(color, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon sx={{ fontSize: 20, color }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '4px' }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '0.85rem', sm: '0.9rem' },
                        lineHeight: 1.3,
                        color: '#1e293b',
                      }}
                    >
                      {title}
                    </Typography>
                    <Chip
                      label="Soon"
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        bgcolor: alpha(color, 0.1),
                        color,
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: { xs: '0.78rem', sm: '0.8rem' },
                      lineHeight: 1.5,
                      color: '#64748b',
                    }}
                  >
                    {description}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Footer ── */}
      <Box
        sx={{
          padding: { xs: '16px 20px 20px', sm: '16px 32px 28px' },
          bgcolor: '#fafafa',
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: { xs: '14px', sm: '16px' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        </Box>
        <Button
          variant="contained"
          onClick={handleClose}
          disableElevation
          size={isMobile ? 'large' : 'medium'}
          sx={{
            bgcolor: '#23395d',
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.85rem',
            borderRadius: '10px',
            textTransform: 'none',
            px: 3,
            py: { xs: 1.25, sm: 1 },
            whiteSpace: 'nowrap',
            flexShrink: 0,
            '&:hover': { bgcolor: '#1a2a4a' },
          }}
        >
          Got it, let&apos;s go!
        </Button>
      </Box>
    </Dialog>
  );
}

export default ComingSoonModal;
